/**
 * Reusable editors for grouped early-years activities.
 *
 * The server still owns validation and rendering. These controls only make the
 * repeatable item data pleasant to author.
 */

import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api/client'
import type { Option, OptionIn } from '../../lib/api/client'
import { Button, IconButton, Input, Radio, Select } from '../../components/ui'
import { Icon } from '../../components/icons'
import { PicturePicker } from './PicturePicker'
import { subitemLabel } from '../../lib/labels'

type ActivityOption = OptionIn & { item_data: Record<string, unknown> }

export function ActivityEditor({
  editorKey,
  mode,
  shuffleSizes,
  options,
  onChange,
}: {
  editorKey: string
  mode?: unknown
  shuffleSizes?: unknown
  options: Option[]
  onChange: (options: OptionIn[]) => void
}) {
  const [draft, setDraft] = useState<ActivityOption[]>(() =>
    toDraft(options, editorKey, mode),
  )
  useEffect(
    () => setDraft(toDraft(options, editorKey, mode)),
    [options, editorKey, mode],
  )

  const pictures = useQuery({
    queryKey: ['pictures', 'all'],
    queryFn: () => api.pictures({ per_page: 100 }),
  })
  const library = pictures.data?.images ?? []

  const commit = (next: ActivityOption[]) => {
    setDraft(next)
    onChange(next)
  }
  const update = (index: number, patch: Partial<ActivityOption>, save = false) => {
    const next = draft.map((item, position) =>
      position === index
        ? {
            ...item,
            ...patch,
            item_data: patch.item_data ?? item.item_data,
          }
        : item,
    )
    setDraft(next)
    if (save) onChange(next)
  }
  const updateData = (index: number, patch: Record<string, unknown>, save = false) =>
    update(index, { item_data: { ...draft[index].item_data, ...patch } }, save)

  if (editorKey === 'missing_number') {
    const relation = String(mode ?? 'between')
    return (
      <ActivityRows
        label="Number rows"
        hint="Add all sub-items under this one instruction."
        onAdd={() =>
          commit([
            ...draft,
            activityOption(
              relation === 'between' ? { left: 1, right: 3 } : { target: 1 },
            ),
          ])
        }
      >
        {draft.map((option, index) => (
          <div className="tc-activity-row" key={options[index]?.id ?? index}>
            <span className="tc-option-letter">{subitemLabel(index)})</span>
            {relation === 'between' ? (
              <>
                <Input
                  type="number"
                  aria-label={`Left number ${index + 1}`}
                  value={String(option.item_data.left ?? '')}
                  onChange={(event) => updateData(index, { left: Number(event.target.value) })}
                  onBlur={() => onChange(draft)}
                />
                <span className="tc-activity-blank">_____</span>
                <Input
                  type="number"
                  aria-label={`Right number ${index + 1}`}
                  value={String(option.item_data.right ?? '')}
                  onChange={(event) => updateData(index, { right: Number(event.target.value) })}
                  onBlur={() => onChange(draft)}
                />
              </>
            ) : (
              <>
                {relation === 'before' && <span className="tc-activity-blank">_____</span>}
                <Input
                  type="number"
                  aria-label={`Number ${index + 1}`}
                  value={String(option.item_data.target ?? '')}
                  onChange={(event) => updateData(index, { target: Number(event.target.value) })}
                  onBlur={() => onChange(draft)}
                />
                {relation === 'after' && <span className="tc-activity-blank">_____</span>}
              </>
            )}
            <RowActions
              index={index}
              onDuplicate={() => commit(duplicateRow(draft, index))}
              onRemove={() => commit(draft.filter((_, i) => i !== index))}
            />
          </div>
        ))}
      </ActivityRows>
    )
  }

  if (editorKey === 'count_pictures') {
    return (
      <ActivityRows
        label="Picture groups"
        hint="Choose one picture and how many times it should appear."
        onAdd={() => commit([...draft, activityOption({ repeat_count: 5 })])}
      >
        {draft.map((option, index) => (
          <div className="tc-activity-row" key={options[index]?.id ?? index}>
            <span className="tc-option-letter">{subitemLabel(index)})</span>
            <PicturePicker
              library={library}
              chosen={option.image_id ?? null}
              onPick={(imageId) => update(index, { image_id: imageId }, true)}
            />
            <Input
              type="number"
              min={1}
              max={50}
              aria-label={`Picture count ${index + 1}`}
              value={String(option.item_data.repeat_count ?? '')}
              onChange={(event) =>
                updateData(index, { repeat_count: Number(event.target.value) })
              }
              onBlur={() => onChange(draft)}
            />
            <span className="tc-field-hint">pictures</span>
            <RowActions
              index={index}
              onDuplicate={() => commit(duplicateRow(draft, index))}
              onRemove={() => commit(draft.filter((_, i) => i !== index))}
            />
          </div>
        ))}
      </ActivityRows>
    )
  }

  if (editorKey === 'compare_sizes') {
    return (
      <ActivityRows
        label="Picture sets"
        hint="Each picture is automatically printed in three sizes."
        onAdd={() => commit([...draft, activityOption({ scales: [0.65, 0.85, 1.05] })])}
      >
        {draft.map((option, index) => (
          <div className="tc-activity-row" key={options[index]?.id ?? index}>
            <span className="tc-option-letter">{subitemLabel(index)})</span>
            <PicturePicker
              library={library}
              chosen={option.image_id ?? null}
              onPick={(imageId) => update(index, { image_id: imageId }, true)}
            />
            <span className="tc-field-hint">
              {shuffleSizes ? 'Mixed size order' : 'Small · medium · large'}
            </span>
            <RowActions
              index={index}
              onDuplicate={() => commit(duplicateRow(draft, index))}
              onRemove={() => commit(draft.filter((_, i) => i !== index))}
            />
          </div>
        ))}
      </ActivityRows>
    )
  }

  if (editorKey === 'write_in_box') {
    const wordCompletion = String(mode ?? 'answer_box') === 'complete_word'
    return (
      <ActivityRows
        label={wordCompletion ? 'Picture words' : 'Picture or text prompts'}
        hint={
          wordCompletion
            ? 'Enter the word with underscores where pupils write, for example _rrow.'
            : 'Each item prints with a square response box.'
        }
        addLabel="Add item"
        onAdd={() => commit([...draft, activityOption({ answer: '' })])}
      >
        {draft.map((option, index) => (
          <div className="tc-activity-row tc-response-editor-row" key={options[index]?.id ?? index}>
            <span className="tc-option-letter">{subitemLabel(index)})</span>
            <PicturePicker
              library={library}
              chosen={option.image_id ?? null}
              onPick={(imageId) => update(index, { image_id: imageId }, true)}
            />
            <Input
              value={option.text ?? ''}
              aria-label={`${wordCompletion ? 'Word pattern' : 'Prompt text'} ${index + 1}`}
              placeholder={wordCompletion ? 'Word with blank, e.g. _rrow' : 'Prompt text (optional)'}
              onChange={(event) => update(index, { text: event.target.value })}
              onBlur={() => onChange(draft)}
            />
            <Input
              value={String(option.item_data.answer ?? '')}
              aria-label={`Answer ${index + 1}`}
              placeholder={wordCompletion ? 'Missing letter, e.g. A' : 'Answer key (optional)'}
              onChange={(event) => updateData(index, { answer: event.target.value })}
              onBlur={() => onChange(draft)}
            />
            <RowActions
              index={index}
              onDuplicate={() => commit(duplicateRow(draft, index))}
              onRemove={() => commit(draft.filter((_, i) => i !== index))}
            />
          </div>
        ))}
      </ActivityRows>
    )
  }

  if (editorKey === 'tick_cross_pictures') {
    return (
      <ActivityRows
        label="Pictures to mark"
        hint="Choose a picture and whether its answer is a tick or a cross."
        addLabel="Add picture"
        onAdd={() => commit([...draft, activityOption({ mark: 'tick' })])}
      >
        {draft.map((option, index) => (
          <div className="tc-activity-row tc-response-editor-row" key={options[index]?.id ?? index}>
            <span className="tc-option-letter">{subitemLabel(index)})</span>
            <PicturePicker
              library={library}
              chosen={option.image_id ?? null}
              onPick={(imageId) => update(index, { image_id: imageId }, true)}
            />
            <Input
              value={option.text ?? ''}
              aria-label={`Caption ${index + 1}`}
              placeholder="Caption (optional)"
              onChange={(event) => update(index, { text: event.target.value })}
              onBlur={() => onChange(draft)}
            />
            <Select
              aria-label={`Expected mark ${index + 1}`}
              value={String(option.item_data.mark ?? 'tick')}
              onChange={(event) => updateData(index, { mark: event.target.value }, true)}
            >
              <option value="tick">Tick (✓)</option>
              <option value="cross">Cross (✗)</option>
            </Select>
            <RowActions
              index={index}
              onDuplicate={() => commit(duplicateRow(draft, index))}
              onRemove={() => commit(draft.filter((_, i) => i !== index))}
            />
          </div>
        ))}
      </ActivityRows>
    )
  }

  if (editorKey === 'circle_correct' || editorKey === 'shape_properties') {
    return (
      <ActivityRows
        label="Picture or text items"
        hint="Each item can use a picture, text, or both."
        addLabel="Add item"
        onAdd={() =>
          commit([
            ...draft,
            activityOption({ choices: ['', '', ''], correct_index: null }),
          ])
        }
      >
        {draft.map((option, index) => {
          const choices = circleChoices(option.item_data)
          const correctIndex = circleCorrectIndex(option.item_data, choices.length)
          return (
            <div
              className="tc-circle-editor-item"
              key={options[index]?.id ?? index}
            >
              <div className="tc-circle-editor-head">
                <span className="tc-option-letter">{subitemLabel(index)})</span>
                <PicturePicker
                  library={library}
                  chosen={option.image_id ?? null}
                  onPick={(imageId) => update(index, { image_id: imageId }, true)}
                />
                <Input
                  value={option.text ?? ''}
                  aria-label={`Prompt text ${index + 1}`}
                  placeholder="Prompt text (optional)"
                  onChange={(event) => update(index, { text: event.target.value })}
                  onBlur={() => onChange(draft)}
                />
                <RowActions
                  index={index}
                  onDuplicate={() => commit(duplicateRow(draft, index))}
                  onRemove={() => commit(draft.filter((_, i) => i !== index))}
                />
              </div>
              <div className="tc-circle-choice-count">
                <span className="tc-field-label">Number of choices</span>
                <Input
                  type="number"
                  min={2}
                  max={6}
                  aria-label={`Number of choices for item ${index + 1}`}
                  value={String(choices.length)}
                  onChange={(event) => {
                    const count = clampCircleChoiceCount(Number(event.target.value))
                    updateData(
                      index,
                      {
                        choices: resizeCircleChoices(choices, count),
                        correct_index:
                          correctIndex !== null && correctIndex < count
                            ? correctIndex
                            : null,
                      },
                      true,
                    )
                  }}
                />
              </div>
              <div
                className="tc-circle-choice-editor"
                role="radiogroup"
                aria-label={`Correct choice for item ${index + 1}`}
              >
                <div className="tc-circle-choice-unmarked">
                  <Radio
                    name={`circle-correct-${options[index]?.id ?? index}`}
                    label="Answer not marked"
                    checked={correctIndex === null}
                    onChange={() =>
                      updateData(index, { correct_index: null }, true)
                    }
                  />
                </div>
                {choices.map((choice, choiceIndex) => (
                  <div className="tc-circle-choice-row" key={choiceIndex}>
                    <Radio
                      name={`circle-correct-${options[index]?.id ?? index}`}
                      label="Correct"
                      checked={correctIndex === choiceIndex}
                      onChange={() =>
                        updateData(index, { correct_index: choiceIndex }, true)
                      }
                    />
                    <Input
                      value={choice}
                      aria-label={`Choice ${choiceIndex + 1} for item ${index + 1}`}
                      placeholder={`Choice ${choiceIndex + 1}`}
                      onChange={(event) => {
                        const next = [...choices]
                        next[choiceIndex] = event.target.value
                        updateData(index, { choices: next })
                      }}
                      onBlur={() => onChange(draft)}
                    />
                  </div>
                ))}
              </div>
            </div>
          )
        })}
      </ActivityRows>
    )
  }

  return null
}

function ActivityRows({
  label,
  addLabel = 'Add row',
  hint,
  onAdd,
  children,
}: {
  label: string
  hint: string
  addLabel?: string
  onAdd: () => void
  children: React.ReactNode
}) {
  return (
    <div className="tc-options tc-activity-editor">
      <div className="tc-options-head">
        <span className="tc-field-label">{label}</span>
        <span className="tc-field-hint">{hint}</span>
      </div>
      {children}
      <Button size="sm" icon={<Icon.plus size={14} />} onClick={onAdd}>
        {addLabel}
      </Button>
    </div>
  )
}

function RowActions({
  index,
  onDuplicate,
  onRemove,
}: {
  index: number
  onDuplicate: () => void
  onRemove: () => void
}) {
  return (
    <span className="tc-activity-actions">
      <IconButton
        label={`Duplicate row ${index + 1}`}
        size="sm"
        variant="ghost"
        onClick={onDuplicate}
      >
        <Icon.copy size={14} />
      </IconButton>
      <IconButton label={`Remove row ${index + 1}`} size="sm" variant="ghost" onClick={onRemove}>
        <Icon.close size={14} />
      </IconButton>
    </span>
  )
}

function activityOption(
  itemData: Record<string, unknown>,
  text = '',
): ActivityOption {
  return {
    text,
    image_id: null,
    is_correct: false,
    item_data: itemData,
  }
}

const LEGACY_CIRCLE_ITEMS: Record<
  string,
  { choices: string[]; correct_index: number }
> = {
  triangle: { choices: ['6', '3', '4'], correct_index: 1 },
  rectangle: { choices: ['6', '8', '4'], correct_index: 2 },
  circle: { choices: ['1', '4', '0'], correct_index: 2 },
  square: { choices: ['5', '4', '6'], correct_index: 1 },
}

function toDraft(
  options: Option[],
  editorKey: string,
  mode: unknown,
): ActivityOption[] {
  return options.map((option) => ({
    text: option.text ?? '',
    image_id: option.image_id ?? null,
    is_correct: option.is_correct,
    item_data: activityItemData(option, editorKey, mode),
  }))
}

function circleChoices(itemData: Record<string, unknown>): string[] {
  const raw = Array.isArray(itemData.choices)
    ? itemData.choices.map((choice) => String(choice))
    : []
  return resizeCircleChoices(raw, Math.max(2, Math.min(6, raw.length || 3)))
}

function circleCorrectIndex(
  itemData: Record<string, unknown>,
  choiceCount: number,
): number | null {
  const value = itemData.correct_index
  return typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= 0 &&
    value < choiceCount
    ? value
    : null
}

function resizeCircleChoices(choices: string[], count: number): string[] {
  const next = choices.slice(0, count)
  while (next.length < count) next.push('')
  return next
}

function clampCircleChoiceCount(value: number): number {
  if (!Number.isFinite(value)) return 2
  return Math.max(2, Math.min(6, Math.round(value)))
}

function circleCorrectItemData(
  option: Option,
  existing: Record<string, unknown>,
): Record<string, unknown> {
  if (Array.isArray(existing.choices)) {
    return {
      ...existing,
      choices: existing.choices.map((choice) => String(choice)),
    }
  }

  const legacy = LEGACY_CIRCLE_ITEMS[String(option.text ?? '').trim().toLowerCase()]
  return legacy
    ? { ...existing, choices: [...legacy.choices], correct_index: legacy.correct_index }
    : { ...existing, choices: ['', '', ''], correct_index: null }
}

function activityItemData(
  option: Option,
  editorKey: string,
  mode: unknown,
): Record<string, unknown> {
  const existing = { ...(option.item_data ?? {}) }
  if (editorKey === 'circle_correct' || editorKey === 'shape_properties') {
    return circleCorrectItemData(option, existing)
  }
  if (editorKey === 'tick_cross_pictures' && existing.mark == null) {
    return { ...existing, mark: 'tick' }
  }
  if (editorKey !== 'missing_number') return existing

  const relation = String(mode ?? 'between')
  const expectedKey = relation === 'between' ? 'left' : 'target'
  if (existing[expectedKey] != null) return existing

  // Compatibility for a row entered while the generic choices editor was shown.
  // "5" becomes a before/after target; "2, 4" becomes a between-number pair.
  const numbers = String(option.text ?? '').match(/-?\d+/g)?.map(Number) ?? []
  if (relation === 'between' && numbers.length >= 2) {
    return { ...existing, left: numbers[0], right: numbers[1] }
  }
  if (relation !== 'between' && numbers.length >= 1) {
    return { ...existing, target: numbers[0] }
  }
  return existing
}

function duplicateRow(rows: ActivityOption[], index: number): ActivityOption[] {
  const source = rows[index]
  const copy: ActivityOption = {
    ...source,
    item_data: { ...source.item_data },
  }
  return [...rows.slice(0, index + 1), copy, ...rows.slice(index + 1)]
}
